import {
  fetchLandingPageContent,
  adminUpdateLandingPageSection,
  adminResetLandingPageSection,
} from '../../util/api';
import { storableError } from '../../util/errors';

// ================ Action Types ================ //
export const FETCH_LANDING_CONTENT_REQUEST =
  'app/AdminLandingPage/FETCH_LANDING_CONTENT_REQUEST';
export const FETCH_LANDING_CONTENT_SUCCESS =
  'app/AdminLandingPage/FETCH_LANDING_CONTENT_SUCCESS';
export const FETCH_LANDING_CONTENT_ERROR =
  'app/AdminLandingPage/FETCH_LANDING_CONTENT_ERROR';

export const UPDATE_LANDING_SECTION_REQUEST =
  'app/AdminLandingPage/UPDATE_LANDING_SECTION_REQUEST';
export const UPDATE_LANDING_SECTION_SUCCESS =
  'app/AdminLandingPage/UPDATE_LANDING_SECTION_SUCCESS';
export const UPDATE_LANDING_SECTION_ERROR =
  'app/AdminLandingPage/UPDATE_LANDING_SECTION_ERROR';

export const RESET_LANDING_SECTION_REQUEST =
  'app/AdminLandingPage/RESET_LANDING_SECTION_REQUEST';
export const RESET_LANDING_SECTION_SUCCESS =
  'app/AdminLandingPage/RESET_LANDING_SECTION_SUCCESS';
export const RESET_LANDING_SECTION_ERROR =
  'app/AdminLandingPage/RESET_LANDING_SECTION_ERROR';

export const SET_ACTIVE_SECTION = 'app/AdminLandingPage/SET_ACTIVE_SECTION';
export const SET_ACTIVE_LANG = 'app/AdminLandingPage/SET_ACTIVE_LANG';

// ================ Initial State ================ //
const initialState = {
  contentDict: {},
  fetchInProgress: false,
  fetchError: null,
  updateInProgress: false,
  updateError: null,
  resetInProgress: false,
  resetError: null,
  activeSection: 'hero',
  activeLang: 'en',
};

// ================ Reducer ================ //
export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;

  switch (type) {
    case FETCH_LANDING_CONTENT_REQUEST:
      return { ...state, fetchInProgress: true, fetchError: null };

    case FETCH_LANDING_CONTENT_SUCCESS:
      return {
        ...state,
        fetchInProgress: false,
        contentDict: payload || {},
        fetchError: null,
      };

    case FETCH_LANDING_CONTENT_ERROR:
      return { ...state, fetchInProgress: false, fetchError: payload };

    case UPDATE_LANDING_SECTION_REQUEST:
      return { ...state, updateInProgress: true, updateError: null };

    case UPDATE_LANDING_SECTION_SUCCESS: {
      const updatedSection = payload || {};
      const sectionKey = updatedSection.sectionKey;
      return {
        ...state,
        updateInProgress: false,
        contentDict: sectionKey
          ? { ...state.contentDict, [sectionKey]: updatedSection }
          : state.contentDict,
        updateError: null,
      };
    }

    case UPDATE_LANDING_SECTION_ERROR:
      return { ...state, updateInProgress: false, updateError: payload };

    case RESET_LANDING_SECTION_REQUEST:
      return { ...state, resetInProgress: true, resetError: null };

    case RESET_LANDING_SECTION_SUCCESS: {
      const sectionKey = payload?.sectionKey;
      const nextDict = { ...state.contentDict };
      if (sectionKey) {
        delete nextDict[sectionKey];
      }
      return {
        ...state,
        resetInProgress: false,
        contentDict: nextDict,
        resetError: null,
      };
    }

    case RESET_LANDING_SECTION_ERROR:
      return { ...state, resetInProgress: false, resetError: payload };

    case SET_ACTIVE_SECTION:
      return { ...state, activeSection: payload };

    case SET_ACTIVE_LANG:
      return { ...state, activeLang: payload };

    default:
      return state;
  }
}

// ================ Action Creators & Thunks ================ //

/**
 * Fetches landing page content overrides from the database.
 */
export const fetchLandingContent = () => async dispatch => {
  dispatch({ type: FETCH_LANDING_CONTENT_REQUEST });
  try {
    const res = await fetchLandingPageContent();
    const data = res?.data || {};
    dispatch({ type: FETCH_LANDING_CONTENT_SUCCESS, payload: data });
    return data;
  } catch (err) {
    const error = storableError(err);
    dispatch({ type: FETCH_LANDING_CONTENT_ERROR, payload: error });
    throw error;
  }
};

/**
 * Updates a specific section's content in the database.
 */
export const updateLandingSection = payload => async dispatch => {
  dispatch({ type: UPDATE_LANDING_SECTION_REQUEST, payload });
  try {
    const res = await adminUpdateLandingPageSection(payload);
    const data = res?.data || res;
    dispatch({ type: UPDATE_LANDING_SECTION_SUCCESS, payload: data });
    return data;
  } catch (err) {
    const error = storableError(err);
    dispatch({ type: UPDATE_LANDING_SECTION_ERROR, payload: error });
    throw error;
  }
};

/**
 * Resets a specific section back to siteData.js defaults.
 */
export const resetLandingSection = payload => async dispatch => {
  dispatch({ type: RESET_LANDING_SECTION_REQUEST, payload });
  try {
    const res = await adminResetLandingPageSection(payload);
    dispatch({
      type: RESET_LANDING_SECTION_SUCCESS,
      payload: { sectionKey: payload.sectionKey },
    });
    return res;
  } catch (err) {
    const error = storableError(err);
    dispatch({ type: RESET_LANDING_SECTION_ERROR, payload: error });
    throw error;
  }
};

/**
 * Switches the actively selected section in the editor sidebar.
 */
export const setActiveSection = sectionKey => ({
  type: SET_ACTIVE_SECTION,
  payload: sectionKey,
});

/**
 * Switches the active language tab (en / ar).
 */
export const setActiveLang = lang => ({
  type: SET_ACTIVE_LANG,
  payload: lang,
});
